import React, { useState, useEffect } from 'react'
import {
  CModal,
  CButton,
  CModalHeader,
  CModalBody,
  CFormInput,
  CForm,
  CModalFooter,
  CDatePicker,
  CMultiSelect,
} from '@coreui/react-pro'
import { useStore } from 'effector-react'
import {
  FilterEvent,
  $transactionsDetails,
  getTransactionsDetails,
} from './model'
import {
  RangeValue,
  TransactionsDetailsItemFromServer,
} from '../TransactionsDetails/types'
import { transactionDetailsType } from '../TransactionsDetails/const'
import { Option } from '@coreui/react-pro/src/components/multi-select/types'
import { TransactionsDetailsRequest } from './types'

import { Currency } from '../../../../../views/widgets/walletCard/types'
import { $wallets } from '../../../wallets/list/model'

type Props = {
  allCurrencies: Currency[]
  isOpen: boolean
  onClose: () => void
}

// Вспомогательная функция для скачивания CSV
const downloadCSV = (
  transactions: TransactionsDetailsItemFromServer[],
  fileName = 'transactions.csv',
) => {
  if (!transactions || transactions.length === 0) {
    console.warn('Нет данных для экспорта в CSV.')
    return
  }

  const headers = Object.keys(transactions[0])
  const csvRows = [
    headers.join(','),
    ...transactions.map((row) =>
      headers
        .map((fieldName) => {
          let value = (row as any)[fieldName]

          // null и undefined → пустая строка
          if (value === null || value === undefined) {
            value = ''
          }

          // массив → строка через ;
          if (Array.isArray(value)) {
            value = value.join(';')
          }

          let stringValue = String(value)

          // убираем переносы строк
          stringValue = stringValue.replace(/[\r\n]+/g, ' ')

          // экранируем и всегда оборачиваем в кавычки
          stringValue = `"${stringValue.replace(/"/g, '""')}"`

          return stringValue
        })
        .join(','),
    ),
  ]
  const csvString = csvRows.join('\n')
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const FilterModal: React.FC<Props> = ({
  isOpen,
  onClose,
  allCurrencies,
}) => {
  const [filtersState, setFiltersState] = useState<{
    [key: string]: string | string[] | RangeValue
  }>({})
  const wallets = useStore($wallets)
  const [dateTime, setDateTime] = useState<{
    dateFrom: Date | null
    dateTo: Date | null
  }>({
    dateFrom: null,
    dateTo: null,
  })

  const [isExporting, setIsExporting] = useState(false)
  const [exportPayload, setExportPayload] =
    useState<TransactionsDetailsRequest | null>(null)

  const currentTransactions = useStore($transactionsDetails)
  const isPending = useStore(getTransactionsDetails.pending)

  const handleDateChange = (
    date: Date | null,
    field: 'dateFrom' | 'dateTo',
  ) => {
    setDateTime((prev) => ({
      ...prev,
      [field]: date,
    }))
  }
  const handleChangeCurrencies = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['currency']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        currency: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeTypes = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['type']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        type: selected.map((item) => item.value as string),
      }))
    }
  }

  const handleChangeWallets = (selected: Option[]) => {
    if (selected.length === 0) {
      const newState = { ...filtersState }
      delete newState['wallets']

      setFiltersState(newState)
    } else {
      setFiltersState((prev) => ({
        ...prev,
        wallets: selected.map((item) => item.value as string),
      }))
    }
  }
  const buildFiltersPayload = (): TransactionsDetailsRequest => {
    const filters: TransactionsDetailsRequest['filters'] = {}
    if (filtersState.currency)
      filters.currency = filtersState.currency as string[]
    if (filtersState.wallets) filters.wallets = filtersState.wallets as string[]
    if (filtersState.type) filters.type = filtersState.type as string[]
    if (dateTime.dateFrom || dateTime.dateTo) {
      filters.period = [dateTime.dateFrom, dateTime.dateTo] as RangeValue
    }
    return {
      filters,
    }
  }
  const handleExportAndDownload = () => {
    const isAnyFilled =
      (filtersState.currency && filtersState.currency.length > 0) ||
      (dateTime.dateFrom && dateTime.dateTo) ||
      (filtersState.type && filtersState.type.length > 0) ||
      (filtersState.wallets && filtersState.wallets.length > 0)
    if (!isAnyFilled) {
      alert('Нужно заполнить хотя бы одно поле')
      return
    }
    const payload = buildFiltersPayload()
    setIsExporting(true)
    setExportPayload(payload)
    FilterEvent(payload)
  }

  useEffect(() => {
    if (!exportPayload || isPending) return

    if (Array.isArray(currentTransactions) && currentTransactions.length > 0) {
      downloadCSV(
        currentTransactions,
        `transactions_export_${new Date().toISOString().slice(0, 10)}.csv`,
      )
    } else {
      console.warn('Нет транзакций для экспорта с указанными фильтрами.')
      alert('Нет транзакций для экспорта с указанными фильтрами.')
    }

    setExportPayload(null)
    setIsExporting(false)
    onClose()
  }, [exportPayload, isPending, currentTransactions, onClose])

  const handleCloseModal = () => {
    if (isExporting) {
      setExportPayload(null)
      setIsExporting(false)
    }
    // сбрасываем фильтры
    setFiltersState({})
    setDateTime({ dateFrom: null, dateTo: null })

    onClose()
  }

  return (
    <CModal visible={isOpen} onClose={handleCloseModal}>
      <CModalHeader style={{ borderBottom: 'none' }}>
        <h4 className="text-center mb-4">Filters</h4>
      </CModalHeader>
      <CModalBody>
        <CForm>
          <CMultiSelect
            className="mb-2"
            virtualScroller
            placeholder="Type"
            size="sm"
            label="Type"
            options={transactionDetailsType.map((item) => ({
              text: item.label,
              value: item.value,
            }))}
            onChange={handleChangeTypes}
          />
          <CMultiSelect
            className="mb-2"
            virtualScroller
            placeholder="Wallet"
            size="sm"
            label="Wallet"
            options={wallets.map((item) => ({
              text: item.name,
              value: item.id,
            }))}
            onChange={handleChangeWallets}
          />
          {allCurrencies && (
            <CMultiSelect
              className="mb-2"
              style={{ width: '335px' }}
              virtualScroller
              label="Currency"
              placeholder="Currency"
              size="sm"
              options={allCurrencies.map((item) => ({
                text: item.code + ' (' + item.name + ')',
                value: item.code,
              }))}
              onChange={handleChangeCurrencies}
            />
          )}
          <CDatePicker
            className="mb-2"
            label="From date"
            onDateChange={(date) => handleDateChange(date, 'dateFrom')}
            disabled={isExporting}
          />
          <CDatePicker
            className="mb-2"
            label="To date"
            onDateChange={(date) => handleDateChange(date, 'dateTo')}
            disabled={isExporting}
          />
        </CForm>
      </CModalBody>
      <CModalFooter className="d-flex flex-column">
        <CButton
          className="w-100"
          onClick={handleExportAndDownload}
          disabled={isExporting}
          color="success"
        >
          {isExporting ? 'Exporting...' : 'Export to CSV'}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}
